/** Waits before the first retries of something that keeps failing (a fetch, a connection); after these, every 30 s. */
const RETRY_DELAYS_MS = [2_000, 5_000, 15_000]
const RETRY_EVERY_MS = 30_000

/** The longest Retry-After a retry waits; the API's throttles name a 1-minute window. */
const RETRY_AFTER_MAX_MS = 60_000

/** Up to this much is added to every retry, so a crowd that failed together doesn't come back together. */
export const RETRY_JITTER_MS = 2_000

/** The backoff alone, for `failures` failures in a row (1 or more): 2 s, 5 s, 15 s, then every 30 s. */
export function retryBackoffMs(failures: number) {
  return RETRY_DELAYS_MS[failures - 1] ?? RETRY_EVERY_MS
}

export interface RetryDelayOptions {
  /** The server's Retry-After (a 429's), waited instead of the backoff; null or left out when it names none. */
  retryAfterMs?: number | null
  /**
   * How the jitter spreads retries: `added` (the default) adds 0–2 s; `proportional` waits half to one and a half times
   * the backoff, for a crowd that dropped together (the room's server going away), which 2 s can't spread once the
   * backoff reaches 30 s. A Retry-After always takes the added jitter: the server's wait is never shortened.
   */
  jitter?: 'added' | 'proportional'
  /** 0 to 1, for the jitter; `Math.random` unless a test pins it. */
  random?: () => number
}

/**
 * How long to wait before trying again after `failures` failures in a row (1 or more): the backoff (2 s, 5 s, 15 s,
 * then every 30 s), or the server's Retry-After when it names one (at most 60 s), plus 0–2 s of jitter, or the backoff
 * spread proportionally (`jitter: 'proportional'`). Used by the watch token, the stage, the chat's history and the
 * room's reconnect (proportional).
 */
export function retryDelayMs(failures: number, { retryAfterMs = null, jitter = 'added', random = Math.random }: RetryDelayOptions = {}) {
  if (retryAfterMs !== null) return Math.min(retryAfterMs, RETRY_AFTER_MAX_MS) + random() * RETRY_JITTER_MS
  const wait = retryBackoffMs(failures)
  return jitter === 'proportional' ? wait * (0.5 + random()) : wait + random() * RETRY_JITTER_MS
}

/**
 * The shortest time between two automatic token refreshes after LiveKit removes the connection. Every
 * refresh brings a new identity, so a removal should not repeat; if it does, the viewer reconnects at
 * this pace instead of in a tight loop.
 */
export const REMOVAL_REFRESH_GAP_MS = 15_000
